import { createContext, useContext, useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { usePageStructureData } from '@/lib/usePageStructureData';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';

// Create context with the same interface as usePageStructureData
const PageStructureContext = createContext<ReturnType<typeof usePageStructureData> | null>(null);

// Separate component that only renders when authenticated
const AuthenticatedPageStructureProvider = ({ 
  children, 
  structureId 
}: { 
  children: React.ReactNode;
  structureId?: string;
}) => {
  const { user } = useAuthStore();
  // Use ref to track initialization state across re-renders
  const isInitializedRef = useRef(false);
  const loadDataRef = useRef<(() => Promise<void>) | null>(null);
  
  const pageStructureData = usePageStructureData({
    userId: user!.id, // We know user exists because this component only renders when authenticated
    structureId,
    autoLoad: false,
  });

  // Store loadData function in ref for stable reference
  loadDataRef.current = pageStructureData.loadData;

  // Load data only once when component mounts
  useEffect(() => {
    // Only initialize once per mount
    if (!isInitializedRef.current && loadDataRef.current) {
      isInitializedRef.current = true;
      
      // Call loadData and handle any errors
      loadDataRef.current()
        .then(() => {
        })
        .catch((error) => {
          console.error('[PageStructureProvider] Error loading page structure data:', error);
          // Reset initialization flag on error to allow retry
          isInitializedRef.current = false;
        });
    }
  }, []); // Empty dependency array - only run once on mount

  // Block rendering until the first load has completed.
  // Check both isLoading and isInitialized to ensure we don't render children
  // before data has been fetched (or confirmed missing) from the API.
  if (pageStructureData.isLoading || !pageStructureData.isInitialized) {
    return (
      <PageStructureContext.Provider value={pageStructureData}>
        <div className="flex flex-col min-h-screen w-full">
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner size="lg" text="Loading page configuration..." />
          </div>
        </div>
      </PageStructureContext.Provider>
    );
  }

  return (
    <PageStructureContext.Provider value={pageStructureData}>
      {children}
    </PageStructureContext.Provider>
  );
};

interface PageStructureProviderProps {
  children: React.ReactNode;
  structureId?: string; // Optional: if provided, loads specific structure
}

export const PageStructureProvider = ({ children, structureId }: PageStructureProviderProps) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <PageStructureContext.Provider value={null}>
        {children}
      </PageStructureContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return (
      <AuthenticatedPageStructureProvider structureId={structureId}>
        {children}
      </AuthenticatedPageStructureProvider>
    );
  }

  // If not authenticated, provide null context
  return (
    <PageStructureContext.Provider value={null}>
      {children}
    </PageStructureContext.Provider>
  );
};

export const usePageStructure = () => {
  const context = useContext(PageStructureContext);
  if (!context) {
    throw new Error('usePageStructure must be used within PageStructureProvider and user must be authenticated');
  }
  return context;
}; 